import React, { createContext, useContext, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DEFAULT_LOCALE, getLocaleFromPathname, locPath, switchLocalePath } from './paths';
import { messages } from './messages';

const LocaleContext = createContext({
  lang: DEFAULT_LOCALE,
  t: (key) => key,
  loc: (path) => locPath(DEFAULT_LOCALE, path),
  switchPath: (next) => locPath(next, '/'),
});

const lookup = (dict, key) => {
  if (!key) return '';
  return key.split('.').reduce((acc, part) => (acc && acc[part] != null ? acc[part] : undefined), dict);
};

export const LocaleProvider = ({ children }) => {
  const { pathname, search, hash } = useLocation();
  const lang = getLocaleFromPathname(pathname);

  const value = useMemo(() => {
    const t = (key) => {
      const translated = lookup(messages[lang], key);
      if (translated != null) return translated;
      const fallback = lookup(messages.en, key);
      return fallback != null ? fallback : key;
    };
    const loc = (path) => locPath(lang, path);
    const switchPath = (nextLang) => switchLocalePath(pathname, nextLang, search, hash);
    return { lang, t, loc, switchPath };
  }, [lang, pathname, search, hash]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
};

export const useLocale = () => useContext(LocaleContext);

export const useLocNavigate = () => {
  const navigate = useNavigate();
  const { loc } = useLocale();
  return (to, options) => navigate(typeof to === 'string' ? loc(to) : to, options);
};
